import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  FlatList,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import APIService from '../services/api';

export interface Departamento {
  id: number; // smallint en la base de datos
  nombre: string; // varchar(60)
}

export interface Municipio {
  id: number; // smallint en la base de datos  
  nombre: string; // varchar(60)
  codigo_postal?: string; // varchar(10)
}

interface DepartmentSelectorProps {
  selectedDepartamento?: Departamento;
  selectedMunicipio?: Municipio;
  onDepartamentoChange: (departamento: Departamento) => void;
  onMunicipioChange: (municipio: Municipio) => void;
  style?: any;
}

const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
  selectedDepartamento,
  selectedMunicipio,
  onDepartamentoChange,
  onMunicipioChange,
  style,
}) => {
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [showDepartamentos, setShowDepartamentos] = useState(false);
  const [showMunicipios, setShowMunicipios] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);

  // Cargar departamentos al montar el componente
  useEffect(() => {
    loadDepartamentos();
  }, []);

  // Debug: Log cuando cambien las props
  useEffect(() => {
    console.log('🔄 DepartmentSelector: selectedDepartamento cambió:', selectedDepartamento);
  }, [selectedDepartamento]);

  useEffect(() => {
    console.log('🔄 DepartmentSelector: selectedMunicipio cambió:', selectedMunicipio);
  }, [selectedMunicipio]);

  // Cargar municipios cuando cambia el departamento
  useEffect(() => {
    if (selectedDepartamento && selectedDepartamento.id && selectedDepartamento.id > 0) {
      loadMunicipios(selectedDepartamento.id, false); // false = no limpiar municipio seleccionado
    } else {
      setMunicipios([]);
    }
  }, [selectedDepartamento]);

  const loadDepartamentos = async () => {
    try {
      setLoading(true);
      console.log('🏛️ Cargando departamentos de Guatemala...');
      const data = await APIService.getDepartamentos();
      console.log('🏛️ Departamentos cargados:', data.length);
      setDepartamentos(data);
    } catch (error: any) {
      console.error('❌ Error cargando departamentos:', error);
      Alert.alert(
        'Error',
        'No se pudieron cargar los departamentos. Verifica tu conexión.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
    }
  };

  const loadMunicipios = async (departamentoId: number, clearSelection: boolean = true) => {
    try {
      setLoadingMunicipios(true);
      console.log('🏛️ Cargando municipios para departamento:', departamentoId);
      
      // Intentar obtener del backend primero
      try {
        const data = await APIService.getMunicipios(departamentoId);
        console.log('🏛️ Municipios cargados desde backend:', data.length);
        setMunicipios(data);
      } catch (backendError) {
        console.warn('⚠️ Backend no disponible, usando datos temporales');
        
        // Fallback con datos locales más completos
        const municipiosLocales: { [key: number]: Municipio[] } = {
          1: [ // Guatemala
            { id: 101, nombre: 'Guatemala', codigo_postal: '01001' },
            { id: 102, nombre: 'Santa Catarina Pinula', codigo_postal: '01002' },
            { id: 103, nombre: 'San José Pinula', codigo_postal: '01003' },
            { id: 104, nombre: 'Mixco', codigo_postal: '01008' },
            { id: 105, nombre: 'Villa Nueva', codigo_postal: '01015' },
            { id: 106, nombre: 'Amatitlán', codigo_postal: '01014' },
            { id: 107, nombre: 'Villa Canales', codigo_postal: '01016' }
          ],
          2: [ // Sacatepéquez
            { id: 201, nombre: 'Antigua Guatemala', codigo_postal: '03001' },
            { id: 202, nombre: 'Jocotenango', codigo_postal: '03002' },
            { id: 203, nombre: 'San Lucas Sacatepéquez', codigo_postal: '03008' },
            { id: 204, nombre: 'Santiago Sacatepéquez', codigo_postal: '03006' }
          ],
          14: [ // Quetzaltenango
            { id: 1401, nombre: 'Quetzaltenango', codigo_postal: '09001' },
            { id: 1402, nombre: 'Salcajá', codigo_postal: '09002' },
            { id: 1403, nombre: 'Olintepeque', codigo_postal: '09003' }
          ]
        };
        
        const municipios = municipiosLocales[departamentoId] || [
          { id: (departamentoId * 100) + 1, nombre: 'Cabecera Municipal', codigo_postal: '00001' }
        ];
        
        console.log('🏛️ Usando municipios locales:', municipios.length);
        setMunicipios(municipios);
      }
      
      // Solo limpiar municipio seleccionado si se indica explícitamente
      if (clearSelection) {
        onMunicipioChange({ id: 0, nombre: '', codigo_postal: '' });
      }
    } catch (error: any) {
      console.error('❌ Error cargando municipios:', error);
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  };

  const handleDepartamentoSelect = (departamento: Departamento) => {
    console.log('📍 Departamento seleccionado:', departamento);
    console.log('📍 onDepartamentoChange function:', onDepartamentoChange);
    console.log('📍 Llamando onDepartamentoChange con:', departamento);
    
    onDepartamentoChange(departamento);
    setShowDepartamentos(false);
    
    // Cargar municipios del departamento seleccionado solo si tiene ID válido
    // clearSelection = true porque el usuario está cambiando manualmente el departamento
    if (departamento.id && departamento.id > 0) {
      console.log('📍 Cargando municipios para departamento ID:', departamento.id);
      loadMunicipios(departamento.id, true);
    }
  };

  const handleMunicipioSelect = (municipio: Municipio) => {
    onMunicipioChange(municipio);
    setShowMunicipios(false);
  };

  const renderDepartamentoItem = ({ item }: { item: Departamento }) => (
    <TouchableOpacity
      style={styles.optionItem}
      onPress={() => handleDepartamentoSelect(item)}
    >
      <Text style={styles.optionText}>{item.nombre}</Text>
    </TouchableOpacity>
  );

  const renderMunicipioItem = ({ item }: { item: Municipio }) => (
    <TouchableOpacity
      style={styles.optionItem}
      onPress={() => handleMunicipioSelect(item)}
    >
      <Text style={styles.optionText}>{item.nombre}</Text>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, style]}>      
      {/* Selector de Departamento */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Departamento *</Text>
        <TouchableOpacity
          style={styles.selector}
          onPress={() => {
            console.log('🖱️ TouchableOpacity presionado, abriendo modal');
            setShowDepartamentos(true);
          }}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#e74c3c" />
          ) : (
            <Text style={styles.selectorText}>
              {selectedDepartamento?.nombre || 'Seleccionar departamento'}
            </Text>
          )}
          <Text style={styles.arrow}>▼</Text>
        </TouchableOpacity>
      </View>

      {/* Selector de Municipio */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Municipio</Text>
        <TouchableOpacity
          style={[
            styles.selector,
            !selectedDepartamento && styles.selectorDisabled,
          ]}
          onPress={() => setShowMunicipios(true)}
          disabled={!selectedDepartamento || loadingMunicipios}
        >
          {loadingMunicipios ? (
            <ActivityIndicator size="small" color="#e74c3c" />
          ) : (
            <Text style={[
              styles.selectorText,
              !selectedDepartamento && styles.selectorTextDisabled,
            ]}>
              {selectedMunicipio?.nombre || 
               (selectedDepartamento ? 'Seleccionar municipio' : 'Primero selecciona departamento')}
            </Text>
          )}
          <Text style={styles.arrow}>▼</Text>
        </TouchableOpacity>
      </View>

      {/* Modal de Departamentos */}
      <Modal
        visible={showDepartamentos}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowDepartamentos(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Seleccionar Departamento</Text>
              <TouchableOpacity
                onPress={() => setShowDepartamentos(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={departamentos}
              renderItem={renderDepartamentoItem}
              keyExtractor={(item) => item.id.toString()}
              style={styles.optionsList}
            />
          </View>
        </View>
      </Modal>

      {/* Modal de Municipios */}
      <Modal
        visible={showMunicipios}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowMunicipios(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Seleccionar Municipio</Text>
              <TouchableOpacity
                onPress={() => setShowMunicipios(false)}
                style={styles.closeButton}
              >
                <Text style={styles.closeButtonText}>✕</Text>
              </TouchableOpacity>
            </View>
            <FlatList
              data={municipios}
              renderItem={renderMunicipioItem}
              keyExtractor={(item) => item.id.toString()}
              style={styles.optionsList}
            />
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  selector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 16,
    minHeight: 50,
  },
  selectorDisabled: {
    backgroundColor: '#f5f5f5',
    borderColor: '#ccc',
  },
  selectorText: {
    fontSize: 16,
    color: '#2c3e50',
    flex: 1,
  },
  selectorTextDisabled: {
    color: '#999',
  },
  arrow: {
    fontSize: 12,
    color: '#999',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '90%',
    maxHeight: '70%',
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  closeButton: {
    padding: 4,
  },
  closeButtonText: {
    fontSize: 20,
    color: '#999',
  },
  optionsList: {
    maxHeight: 400,
  },
  optionItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  optionText: {
    fontSize: 16,
    color: '#2c3e50',
  },
});

export default DepartmentSelector;
